import type { BuiltInTool } from './types'

/**
 * The tool a task uses to end the execution it belongs to (issue #810), when it finds the work already done or
 * abandoned elsewhere. A family of its own, not one of the execution labels: those only write an event on the
 * calling run, and their dispatcher answers nothing else, while this one cancels every open run in the execution.
 */
export const OS_EXECUTION_STOP_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.stop_execution',
        family: 'os.execution_stop',
        summary: 'End the whole execution this run belongs to, because its work is already done or abandoned.',
        description:
            'End the whole execution this run belongs to: every open run in it, this one included, ends cancelled, their gates close and nothing new starts. Call it when the work is already done or abandoned elsewhere. Pass reason, a short text shown on the execution (e.g. pr_merged), and needsHuman: false ends the pipeline clean with no alert, true leaves it blocked for a human.',
        inputSchema: {
            type: 'object',
            properties: {
                reason: { type: 'string', description: 'Why the execution stops, shown on Executions, e.g. pr_merged' },
                needsHuman: {
                    type: 'boolean',
                    description: 'true leaves the work blocked for a human; false ends it clean',
                },
            },
            required: ['reason', 'needsHuman'],
            additionalProperties: false,
        },
        returns: 'The reason as stored (trimmed) and needsHuman.',
        callers: ['run', 'api'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'A repeat call on an execution already stopped changes nothing and succeeds.',
        errors: [
            { code: 'reason_required', when: '`reason` is missing, or blank once trimmed.', handling: 'tool_error' },
            { code: 'reason_invalid', when: '`reason` is not text.', handling: 'tool_error' },
            { code: 'reason_too_long:160', when: '`reason` is over 160 characters.', handling: 'tool_error' },
            { code: 'needs_human_required', when: '`needsHuman` is missing.', handling: 'tool_error' },
            { code: 'needs_human_invalid', when: '`needsHuman` is not true or false.', handling: 'tool_error' },
            { code: 'run_not_active', when: 'The calling run is not running.', handling: 'tool_error' },
        ],
        api: { method: 'POST', path: '/v1/runs/self/stop-execution', scope: 'run:self' },
        examples: [{ input: { reason: 'pr_merged', needsHuman: false } }],
        public: true,
    },
]
